/**
 * CreditWalletCard — 마이페이지 크레딧 카드
 *
 *  기획 개편: 기존 프로필 카드 우측의 "간식 주머니"(CreditPouch) 를
 *  상호명 텍스트 줄 아래 독립 카드로 승격.
 *
 *  구성:
 *   - 보유 크레딧 (총 잔액) + 충전하기 버튼
 *   - 구매 크레딧(paidBalance) / 무료 크레딧(freeBalance) 분리 표시
 *
 *  동작:
 *   - 카드 영역 탭 → 크레딧 상세 화면
 *   - 충전하기 → 충전(웹) 화면
 *
 *  데이터는 전부 useCompanyCreditWallet 응답에 포함 (추가 호출 없음).
 */
import type { CompanyCreditWallet } from "~/services/business/credit";
import { CREDIT } from "%/credit";

interface Props {
  wallet?: CompanyCreditWallet;
  /** 카드 영역 탭 — 크레딧 상세 화면 이동 */
  onCardClick: () => void;
  /** 충전하기 — 충전(웹) 화면 이동 */
  onChargeClick: () => void;
}

export const CreditWalletCard = ({ wallet, onCardClick, onChargeClick }: Props) => {
  const balance = wallet?.balance ?? 0;
  const paid = wallet?.paidBalance ?? 0;
  const free = wallet?.freeBalance ?? 0;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onCardClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onCardClick();
        }
      }}
      aria-label={`${CREDIT.walletName} ${balance.toLocaleString()} ${CREDIT.unit}`}
      className="bg-white rounded-2xl border border-[#F0F0F0] shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-4 cursor-pointer transition-transform active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF9934]"
    >
      {/* 보유 크레딧 + 충전하기 */}
      <div className="flex items-end justify-between">
        <p className="text-[28px] font-bold text-[#111] leading-none tabular-nums">
          {balance.toLocaleString()}
          <span className="text-[13px] font-medium text-[#888] ml-1">
            {CREDIT.unit}
          </span>
        </p>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onChargeClick();
          }}
          className="shrink-0 text-[13px] font-semibold text-[#FF9934] border border-[#FF9934] rounded-lg px-3.5 py-1.5 transition-transform active:scale-95"
        >
          {CREDIT.actions.charge}하기
        </button>
      </div>

      {/* 구매 / 무료 분리 */}
      <div className="border-t border-[#F0F0F0] mt-3 pt-3 space-y-1.5">
        <div className="flex justify-between text-[13px]">
          <span className="text-[#888]">구매 {CREDIT.unit}</span>
          <span className="font-semibold text-[#333] tabular-nums">{paid.toLocaleString()}</span>
        </div>
        <div className="flex justify-between text-[13px]">
          <span className="text-[#888]">무료 {CREDIT.unit}</span>
          <span className="font-semibold text-[#333] tabular-nums">{free.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
};
